/**
 * Copy for `/` — the root homepage (src/pages/marketing/HomeVariantBPage.tsx).
 *
 * WHY THIS MODULE EXISTS, and it is a cutover-specific reason rather than a
 * general tidiness one. `/` has THREE crawler-facing surfaces and until the
 * Phase 2 cutover they were maintained by hand, independently, in three files:
 *
 *   1. the React page          — what a human (and Googlebot) sees after hydration
 *   2. index.html's #seo-content — the SPA-fallback body
 *   3. getFAQSchema()          — the FAQPage JSON-LD injected for `/`
 *
 * All three carried DIFFERENT headlines on 2026-09-11: the page said "Your
 * companion for mastering law school and the bar exam.", index.html said "Your
 * companion for mastering law school." and the FAQ schema still enumerated the
 * SEVEN questions of a page that no longer exists. Nothing in the repo compared
 * them, because each was a hand-written literal in its own file.
 *
 * So the strings live HERE and every surface imports them. Drift is not
 * prevented by a test in that arrangement — it is prevented by there being one
 * string. (.claude/rules/propagate-the-fix.md §3: one source of truth for a
 * cross-artifact constant, everything else points at it.)
 *
 * ── WHAT IS AND IS NOT IN SCOPE FOR THIS MODULE ─────────────────────────────
 * IN: the hero H1/eyebrow/sub, the five product tiles, the carry-forward band's
 * argument, and the five company-level FAQ Q&As. Those are the strings that
 * have to appear identically in the page, the prerendered body and the schema.
 *
 * OUT: layout copy that only one surface renders (section headings, the schools
 * strip, the payoff band's prose, bullet-level microcopy that is purely visual).
 * Hoisting those would move markup decisions into a data file for no drift
 * benefit — the crawler body does not render them.
 *
 * ALSO OUT: the two inherited sections (NonServicesSection, AboutSection) and
 * the testimonials. Those are rendered from their OWN components, which already
 * own their founder-approved copy. Copying them here would create the second
 * source of truth this module exists to remove.
 *
 * ── CLAIMS THAT ARE LOAD-BEARING, NOT STYLE ─────────────────────────────────
 * Every one of these was decided elsewhere and is reproduced, not authored:
 *
 *   - THE H1 IS ARTBOARD 2a's, restored 2026-09-08 after a founder correction
 *     ("this isnt the headline we agreed on"). Do not substitute the
 *     "Stop guessing what to study" hinge — that is `/lp/nextgen-practice-a`'s,
 *     approved for that surface only.
 *   - FLASHCARDS: the TOOL is free, the premium DECKS are not. The whole route
 *     is free (`FlashcardsPageWrapper` is a passthrough; `flashcards` is in the
 *     free group of `isPremiumFeature`) — the gate is PER CARD, on the ANSWER of
 *     an `is_official` card. Route-gating it was the 2ed1d7d8 (2025-08-31)
 *     regression, reverted 2026-09-11. "All flashcards
 *     free" and "flashcards are premium" are both banned
 *     (.claude/rules/free-tier-value-claims.md guardrail 2). The approved
 *     sentence appears verbatim in BOTH the free tile and the FAQ answer — one
 *     string, used twice, is the point.
 *   - CARRY-FORWARD HAS A CEILING: knowledge and progress carry; NextGen
 *     exam-readiness and format scores do NOT. Approved: "your knowledge and
 *     progress carry over", "you never start cold". Banned: "your readiness is
 *     already built".
 *   - ASK JDS IS 30 MESSAGES/MONTH on the free plan (`FREE_MESSAGE_LIMIT`).
 *     "Unlimited free chat" is banned.
 *   - THE PACKS ARE NEVER BUNDLED into JDS Unlimited or NextGen Practice
 *     (.claude/rules/ncbe-licensed-content.md §IV.B). They ARE part of the
 *     NextGen Course — founder decision 2026-09-07, re-confirmed 2026-09-09 —
 *     and /bar/course owns the Course's terms.
 *   - "NextGen Practice", never "NextGen Prep" and never "JDS NextGen Practice".
 *
 * ── THE NATIVE SPLIT ────────────────────────────────────────────────────────
 * Pure Client v1 hides dollar prices and checkout affordances in the native
 * shell (docs/mobile-app/STORE-PURCHASE-STRATEGY.md), so two strings have a
 * native variant. The PRERENDERED body and the JSON-LD always take the WEB
 * form: prerendered HTML is served to web crawlers and never inside the native
 * shell, and JSON-LD has no native consumer at all.
 */

/** A single FAQ entry. `answerNative` is present only where prices force a split. */
export interface HomeFaq {
  readonly question: string;
  readonly answer: string;
  /** Native-shell form, when the web form names a dollar price. */
  readonly answerNative?: string;
}

/** One product tile on the path rail. */
export interface HomeTile {
  /** Price or availability line above the name. */
  readonly kicker: string;
  /** Native-shell kicker, where the web form names a dollar price. */
  readonly kickerNative?: string;
  readonly name: string;
  readonly href: string;
  readonly bullets: readonly string[];
  /** Rendered in the bordered "Includes" row. */
  readonly includes?: string | readonly string[];
}

export const HOME_ROOT_COPY = {
  eyebrow: '1L through the bar exam',

  /**
   * ARTBOARD 2a's H1. See the module docblock — this exact string was restored
   * after a founder correction and must not be re-hinged.
   *
   * There is no duplication with `/law-school/unlimited`, which is law-school
   * scoped and takes "Your companion for thriving in law school." This page
   * spans 1L THROUGH the bar, which is what "and the bar exam" carries.
   */
  heading: 'Your companion for mastering law school and the bar exam.',

  subheading:
    'Case briefs, outlines, practice, and feedback in one account, from your first case to the bar exam.',

  /**
   * The <title> on `/`, for BOTH heads — HomeVariantBPage's `useSEO` (hydrated)
   * and `getHomepageSEO()` in src/seo/payloads.ts (prerendered). Naming both is
   * the point: `getHomepageSEO()` hardcoded its own pair until 2026-09-11, so
   * the crawler-visible head and the rendered head advertised different titles
   * on the same URL.
   *
   * 54 chars. scripts/prerender.ts FAILS the build above 65 — it does not warn
   * — so check the length when editing this. (This said "58 chars", which was
   * wrong; the number was never re-counted after the string was settled. It had
   * margin either way, but a stale count in a comment about a hard gate is the
   * kind of thing someone edits up against.) Pinned in homepage-jsonld.test.ts.
   */
  seoTitle: 'JD Simplified — law school and bar prep in one account',
  seoDescription:
    'Case briefs, the outline library, practice, and feedback in one account, from your first case to the bar exam. Free to start, no card required.',

  /** The two stage markers on the rail. */
  stages: {
    lawSchool: '1L – 3L · Law school',
    bar: 'Bar exam',
  },

  /**
   * THE FIVE PRODUCT TILES, in rail order.
   *
   * FIVE, not four. The founder picked the five-tile concept and explicitly
   * REJECTED burying the free tile ("it's easy to miss the 'always free' tier"),
   * then asked twice why only four products were showing.
   *
   * `packs` is behind the `nextgen-official-questions` kill switch in the React
   * page, and is OMITTED from the prerendered body for the same reason: the
   * route redirects to /bar when the flag is prod-OFF, and a crawler-visible
   * card advertising a bouncing route is the loop the page's docblock note 4
   * describes. Prerendered HTML is generated at BUILD time with no flag context,
   * so there is no "resolve the flag" option — omitting it is the honest one.
   */
  tiles: {
    free: {
      kicker: 'Always Free',
      name: 'JD Simplified',
      href: '/law-school',
      bullets: [
        'Case Briefs',
        'Notes & Outlines',
        // Names the TOOL, which is free. The paid tile names the CONTENT.
        // Founder 2026-09-09: "flashcard/study mode functionality is free.
        // Premium flashcard content is not."
        'Flashcards',
        '30 Ask JDS messages per month',
      ],
      /** Present-tense companion to "Always Free" — what the tier IS, not what it will remain. */
      note: 'No trial, no time limit, no credit card.',
    },
    unlimited: {
      kicker: '$20 / month',
      kickerNative: 'Paid plan',
      name: 'JDS Unlimited',
      href: '/law-school/unlimited',
      includes: 'JD Simplified',
      bullets: [
        // FIRST by founder direction (2026-09-11), and scoped to THIS tile
        // only — not LawSchoolUnlimitedPage, not PricingPage, not
        // HomepagePricingSection, not the FAQ answers.
        //
        // "Case Law Comparatives" is the FINAL feature name (founder ruling
        // 2026-09-11, superseding "Comparative Case Analysis", which shipped in
        // e315474d earlier the same day and reached no other surface).
        'Case Law Comparatives',
        'Issue Spotter',
        'Essay Coach',
        'Outline IQ',
        'Unlimited Ask JDS',
        'Video Library',
        // The CONTENT half of the gate-the-content split.
        'Premium Flashcards & Study Content',
      ],
    },
    practice: {
      // "From $499", never "$499 one-time": $499 buys ONE exam cycle and later
      // cycles cost more.
      kicker: 'From $499',
      kickerNative: 'One-time purchase',
      name: 'NextGen Practice',
      href: '/bar/practice',
      includes: 'JDS Unlimited',
      bullets: [
        'Directed practice',
        'Skill diagnostics',
        'Full exam simulations',
        'Works alongside any bar course',
      ],
    },
    packs: {
      kicker: 'Coming Soon',
      name: 'Official NCBE Question Packs',
      href: '/bar/ncbe-nextgen-questions',
      bullets: [
        'NCBE NextGen question sets',
        'No course required',
        // NO "A separate purchase" bullet — removed by the founder 2026-09-09.
        // §IV.B bans claiming the Packs are INCLUDED with anything; it does not
        // require affirmatively stating "separate" on every surface. This tile
        // makes no inclusion claim. Do NOT replace it with any "includes"
        // phrasing. The separateness statement still lives where the terms do,
        // on /bar and /bar/ncbe-nextgen-questions.
      ],
    },
    course: {
      kicker: 'Coming March 2027',
      name: 'NextGen Course',
      href: '/bar/course',
      // The Packs ARE part of the Course. Founder 2026-09-07, re-confirmed
      // 2026-09-09. §IV.B's royalty is a COST of the bundle, not a bar on it.
      // /bar/course owns the Course's terms and states the same thing.
      includes: ['JDS Unlimited', 'NextGen Practice', 'Official NCBE Question Packs'],
      bullets: ['Full video course', 'Same practice-first system'],
    },
  },

  /** The Studicata partner mark, in its LOCKED standalone form. */
  studicataMark: 'In collaboration with Studicata',

  /** The ELSEWHERE / HERE carry-forward band. */
  carryForward: {
    eyebrow: 'One account, 1L through the bar',
    heading: 'Start bar prep with three years of your own work already in it.',
    body:
      'Elsewhere, study aids and bar review are separate products with separate histories. ' +
      'Whatever you did in 1L stays there. Here it’s one account: your case briefs, ' +
      'essays, and study history are still with you when bar prep starts, so your knowledge ' +
      'and progress carry over and you never start cold.',
    /**
     * THE CEILING SENTENCE. Not optional and not a caveat to trim: without it
     * the paragraph above reads as a claim that bar-readiness carries over,
     * which is the one thing .claude/rules/free-tier-value-claims.md forbids.
     */
    ceiling:
      'Your NextGen exam-readiness and format scores are a separate thing — those are built ' +
      'by NextGen practice itself, and measured there rather than inherited.',
  },

  /**
   * THE COMPANY-LEVEL FAQ — five questions.
   *
   * A SUBSET, and the cut map is stated in /law-school/unlimited's own source:
   * the company-level questions stay on the homepage; that page answers what you
   * get and what it costs. The two product-level questions the OLD homepage
   * carried ("Do I need a credit card to start?", "How much does JD Simplified
   * cost?") are DELIBERATELY ABSENT — /law-school/unlimited already answers both.
   * Duplicating them puts two competing answer sets in front of one crawler,
   * which is exactly what the namespace split exists to stop.
   *
   * getFAQSchema() in src/seo/schema-helpers.ts maps over THIS array, so the
   * FAQPage JSON-LD for `/` and the page's rendered <details> cannot disagree.
   * That is the whole fix for board task JMnyYLzSUtBx8BbVbRSl: before this, the
   * schema was a hand-written object literal enumerating seven Q&As and nothing
   * compared it to any page.
   */
  faqs: [
    {
      question: 'What is JD Simplified?',
      answer:
        'JD Simplified is a practice-first learning system for law students and bar takers. It brings your case briefs, outlines, notes, flashcards, and Ask JDS into one connected place — so you can understand the law, practice applying it, and focus on what actually matters.',
    },
    {
      question: 'Is it really free?',
      // "Full study mode and your own flashcards — premium decks unlock with a
      // plan" is the APPROVED SENTENCE VERBATIM, the same one the free tile
      // carries. As of 2026-09-11 it is if anything UNDER-claiming: the whole
      // flashcards route is free, so creating, editing, collections and subjects
      // are all free alongside study mode (the route gate was the 2ed1d7d8
      // regression). Only premium `is_official` ANSWERS are gated, per card.
      // The answer below stays as written — it is still true.
      answer:
        'Yes. You can study, take notes, build outlines, and use core tools on the free plan, with no time limit. Full study mode and your own flashcards — premium decks unlock with a plan. Unlimited ($20/month or $200/year) adds those decks, courses, Issue Spotter, Essay Coach, Outline IQ, and unlimited Ask JDS.',
      answerNative:
        'Yes. You can study, take notes, build outlines, and use core tools on the free plan, with no time limit. Full study mode and your own flashcards — premium decks unlock with a plan. The Unlimited plan adds those decks, courses, Issue Spotter, Essay Coach, Outline IQ, and unlimited Ask JDS. Plans are managed from your account on the web.',
    },
    {
      question: 'Can JD Simplified help with the bar exam?',
      answer:
        "Yes. The same skills you build in law school — issue-spotting, applying rules, writing clear analysis — are what the bar tests. When you're ready, NextGen Practice is our practice-first prep built around how the NextGen bar exam actually works.",
    },
    {
      question: "What's the difference between JD Simplified and NextGen Practice?",
      answer:
        'JD Simplified is your day-to-day system for law school. NextGen Practice is practice-first bar prep for the NextGen UBE, built in collaboration with Studicata. One account carries you from your first case brief through bar exam day.',
    },
    {
      question: 'Will my work carry over when I start bar prep?',
      // "inside NextGen Prep's own practice" would rename to "NextGen Practice's
      // own practice", which is not English. "in NextGen Practice itself" carries
      // the identical claim: readiness is measured natively, never inherited.
      answer:
        'Your knowledge and progress carry over — the studying you do in JD Simplified builds on into NextGen Practice, so you start ahead instead of from scratch. (Your bar-readiness is measured fresh in NextGen Practice itself.)',
    },
  ] satisfies readonly HomeFaq[],
} as const;

/**
 * The FAQ in the form every non-native surface wants: the web answer, always.
 *
 * The prerendered body and the JSON-LD both use this. Neither has a native
 * consumer — prerendered HTML is served to web crawlers only, and JSON-LD is
 * read by search engines — so resolving the split here rather than at each call
 * site removes the chance of one of them picking the wrong branch.
 */
export const HOME_ROOT_FAQS_WEB: ReadonlyArray<{ question: string; answer: string }> =
  HOME_ROOT_COPY.faqs.map((f) => ({ question: f.question, answer: f.answer }));
